import { createFileRoute } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { useState, useRef, useCallback } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { enrichCnpj } from "@/lib/cnpj.functions";
import { POLOS } from "@/constants/polos";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { 
  Upload, 
  FileText, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle, 
  Download,
  Info,
  ChevronRight,
  ArrowRight,
  Plus,
  ShieldCheck
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { FORNECEDOR_ORIGEM, FORNECEDOR_STATUS } from "@/constants/enums";

export const Route = createFileRoute("/admin/importar")({
  component: ImportarAdmin,
});

const COLUNAS_OBRIGATORIAS = ["nome", "segmento"];
const COLUNAS_OPCIONAIS = ["cnpj", "cidade", "uf", "whatsapp", "razao_social", "email", "site", "instagram", "polo", "pedido_minimo", "atende_varejo", "descricao"];

function ImportarAdmin() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState(1);
  const [rawData, setRawData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importProgress, setImportProgress] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const [results, setResults] = useState<{ inseridos: number; atualizados: number; ignorados: number; erros: any[]; abortError?: any; criadosAuto?: string[] } | null>(null);

  const [importConfig, setImportConfig] = useState<{
    status: string;
    verificado: boolean;
    politicaDuplicados: "ignorar" | "atualizar";
    enriquecerCnpj: boolean;
  }>({
    status: FORNECEDOR_STATUS.APROVADO,
    verificado: false,
    politicaDuplicados: "ignorar" as "ignorar" | "atualizar",
    enriquecerCnpj: true
  });

  const { data: segmentos } = useQuery({
    queryKey: ["segmentos"],
    queryFn: async () => {
      const { data } = await supabase.from("segmentos").select("*");
      return data || [];
    }
  });

  const { data: fornecedoresExistentes } = useQuery({
    queryKey: ["admin-existing-fornecedores"],
    queryFn: async () => {
      const { data } = await supabase.from("fornecedores").select("id, cnpj, nome, site");
      return data || [];
    }
  });

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name;
    const isExcel = fileName.endsWith(".xlsx") || fileName.endsWith(".xls");

    if (isExcel) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const bstr = evt.target?.result;
        if (typeof bstr !== 'string') return;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        if (!wsname) return;
        const ws = wb.Sheets[wsname];
        if (!ws) return;
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][];
        if (Array.isArray(data) && data.length > 0) {
          const firstRow = data[0];
          if (Array.isArray(firstRow)) {
            processHeadersAndData(firstRow as string[], data.slice(1));
          }
        }
      };
      reader.readAsBinaryString(file);
    } else {
      Papa.parse(file, {
        complete: (results: any) => {
          if (results.data.length > 0) {
            processHeadersAndData(results.data[0] as string[], results.data.slice(1));
          }
        },
        header: false,
        skipEmptyLines: true,
      });
    }
  };

  const processHeadersAndData = (headerRow: string[], dataRows: any[]) => {
    setHeaders(headerRow);
    const data = dataRows.map(row => {
      const obj: any = {};
      headerRow.forEach((h, i) => {
        obj[h] = row[i];
      });
      return obj;
    });
    setRawData(data);

    // Auto mapping
    const newMapping: Record<string, string> = {};
    const targets = [...COLUNAS_OBRIGATORIAS, ...COLUNAS_OPCIONAIS];
    
    headerRow.forEach(h => {
      const normalizedHeader = h.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
      const match = targets.find(t => {
        const normalizedTarget = t.toLowerCase();
        return normalizedHeader.includes(normalizedTarget) || normalizedTarget.includes(normalizedHeader);
      });
      if (match) newMapping[h] = match;
    });
    
    setMapping(newMapping);
    setStep(2);
  };

  const validateLine = (line: any) => {
    const mappedLine: any = {};
    Object.entries(mapping).forEach(([csvHeader, dbField]) => {
      mappedLine[dbField] = line[csvHeader];
    });

    const cnpj = String(mappedLine.cnpj || "").replace(/\D/g, "");
    const errors: string[] = [];
    const warnings: string[] = [];

    // Deduplication check
    const normalizedName = mappedLine.nome ? mappedLine.nome.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "") : "";
    const normalizedDomain = mappedLine.site ? mappedLine.site.toLowerCase().replace(/^(https?:\/\/)?(www\.)?/, "").split('/')[0] : "";

    const duplicate = fornecedoresExistentes?.find(f => {
      if (cnpj && cnpj.length === 14 && f.cnpj && f.cnpj.replace(/\D/g, "") === cnpj) return true;
      if (!cnpj || cnpj.length !== 14) {
        const existingName = f.nome.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
        const existingDomain = f.site ? f.site.toLowerCase().replace(/^(https?:\/\/)?(www\.)?/, "").split('/')[0] : "";
        return normalizedName === existingName && normalizedDomain === existingDomain && normalizedName !== "";
      }
      return false;
    });

    if (duplicate) {
      warnings.push(`Provável duplicado: ${duplicate.nome}`);
    }

    if (cnpj && cnpj.length !== 14) {
      warnings.push("CNPJ informado é inválido e será ignorado");
    }

    const segmentName = String(mappedLine.segmento || "").toLowerCase().trim();
    const segment = segmentos?.find(s => 
      s.nome.toLowerCase() === segmentName || 
      (s.slug && s.slug === segmentName.replace(/\s+/g, '-')) ||
      (s.aliases && Array.isArray(s.aliases) && s.aliases.some((a: string) => a.toLowerCase() === segmentName))
    );

    if (!segment) {
      warnings.push(`Segmento "${mappedLine.segmento}" será criado automaticamente`);
    }

    return { errors, warnings, segmentId: segment?.id };
  };

  const startImport = async () => {
    setIsImporting(true);
    setStep(4);
    
    let inseridos = 0;
    let atualizados = 0;
    let ignorados = 0;
    let criadosAuto: string[] = [];
    const erros: any[] = [];

    const total = rawData.length;
    const toProcess = [...rawData];

    const deduzePolo = (bairro: string = "", municipio: string = "", logradouro: string = "") => {
      const b = (bairro || "").toLowerCase();
      const m = (municipio || "").toLowerCase();
      const l = (logradouro || "").toLowerCase();

      if (b.includes("bras")) return "Brás";
      if (b.includes("bom retiro")) return "Bom Retiro";
      if (b.includes("santa efigenia") || b.includes("santa ifigenia")) return "Santa Ifigênia";
      if (l.includes("vinte e cinco de marco") || l.includes("25 de marco")) return "25 de Março";

      if (m.includes("limeira")) return "Limeira";
      if (m.includes("franca")) return "Franca";
      if (m.includes("nova friburgo")) return "Nova Friburgo";
      if (m.includes("caruaru")) return "Caruaru";
      if (m.includes("goiania")) return "Goiânia";
      if (m.includes("cianorte")) return "Cianorte";
      if (m.includes("nova serrana")) return "Nova Serrana";

      return null;
    };

    // 1. Get real column names from schema to avoid "schema cache" errors
    const { data: tableInfo } = await supabase.rpc('contagem_publica'); // Just to wake up the connection, we'll use a safer approach for schema validation
    
    // Better way: get columns from a sample insert if needed, but here we'll just be explicit.
    // The error says 'segmento' was sent but table only has 'segmento_id'.
    const VALID_FORNECEDORES_COLUMNS = [
      'nome', 'cnpj', 'segmento_id', 'cidade', 'uf', 'telefone', 'email', 'site', 
      'instagram', 'razao_social', 'polo', 'pedido_minimo', 'atende_varejo', 
      'descricao', 'status', 'verificado', 'origem', 'logo_url', 'owner_id'
    ];

    // 2. Validate first row as a dry run to abort early on systemic errors
    if (toProcess.length > 0) {
      const firstLine = toProcess[0];
      const { errors: firstErrors, segmentId: firstSegmentId } = validateLine(firstLine);
      
      if (firstErrors.length === 0) {
        const testPayload: any = {
          nome: "TESTE_IMPORTACAO",
          cnpj: "00000000000000",
          segmento_id: firstSegmentId,
          status: FORNECEDOR_STATUS.PENDENTE, // Safe status for test
          origem: FORNECEDOR_ORIGEM.IMPORTADO
        };
        
        const { error: testError } = await supabase.from("fornecedores").insert(testPayload).select().single();
        
        if (testError) {
          console.error("Erro completo do Supabase no insert de teste:", {
            message: testError.message,
            details: testError.details,
            hint: testError.hint,
            code: testError.code
          });
          
          await supabase.from("fornecedores").delete().eq("cnpj", "00000000000000").eq("nome", "TESTE_IMPORTACAO");
          
          toast.error(`Erro no teste: ${testError.message}`);
          setResults({ 
            inseridos: 0, 
            atualizados: 0, 
            ignorados: 0, 
            erros: [], 
            abortError: testError 
          });
          setIsImporting(false);
          return;
        } else {
          // Success, delete the test row
          await supabase.from("fornecedores").delete().eq("cnpj", "00000000000000").eq("nome", "TESTE_IMPORTACAO");
        }
      }
    }

    for (let index = 0; index < toProcess.length; index++) {
      const line = toProcess[index];
      const { errors: lineErrors, warnings: lineWarnings } = validateLine(line);
      
      if (lineErrors.length > 0) {
        erros.push({ line, errors: lineErrors });
        continue;
      }
      
      // Resolve/Create Segment
      const segmentNameValue = line[Object.keys(mapping).find(k => mapping[k] === 'segmento')!];
      const { data: segmentId, error: segmentError } = await supabase.rpc('resolver_segmento', { p_nome: segmentNameValue });
      if (segmentError) {
        erros.push({ line, errors: [segmentError.message] });
        continue;
      }

      // Check if it was newly created (id not in local cache)
      if (!segmentos?.find(s => s.id === segmentId)) {
        if (!criadosAuto.includes(segmentNameValue)) {
          criadosAuto.push(segmentNameValue);
        }
      }

      const rawMapped: any = {};
      Object.entries(mapping).forEach(([csvHeader, dbField]) => {
        if (dbField !== "ignore") {
          rawMapped[dbField] = line[csvHeader];
        }
      });

      // Prepare final payload
      const payload: any = {
        status: importConfig.status,
        verificado: importConfig.verificado,
        origem: FORNECEDOR_ORIGEM.IMPORTADO,
        segmento_id: segmentId,
        cnpj: String(rawMapped.cnpj || "").replace(/\D/g, "") || null
      };

      // Map basic fields
      if (rawMapped.nome) payload.nome = rawMapped.nome;
      if (rawMapped.cidade) payload.cidade = rawMapped.cidade;
      if (rawMapped.uf) payload.uf = rawMapped.uf;
      if (rawMapped.email) payload.email = rawMapped.email;
      if (rawMapped.site) payload.site = rawMapped.site;
      if (rawMapped.instagram) payload.instagram = rawMapped.instagram;
      if (rawMapped.razao_social) payload.razao_social = rawMapped.razao_social;
      if (rawMapped.polo) payload.polo = rawMapped.polo;
      if (rawMapped.pedido_minimo) payload.pedido_minimo = rawMapped.pedido_minimo;
      if (rawMapped.atende_varejo !== undefined) payload.atende_varejo = rawMapped.atende_varejo;
      if (rawMapped.descricao) payload.descricao = rawMapped.descricao;

      // Handle WhatsApp/Telefone
      const phone = String(rawMapped.whatsapp || rawMapped.telefone || "").replace(/\D/g, "");
      if (phone) payload.telefone = phone;

      // CNPJ Enrichment
      if (importConfig.enriquecerCnpj && payload.cnpj.length === 14) {
        try {
          const result = await enrichCnpj({ data: { cnpj: payload.cnpj } });
          if (result.success && result.data) {
            const info = result.data;
            if (!payload.razao_social) payload.razao_social = info.razao_social;
            if (!payload.cidade) payload.cidade = info.municipio;
            if (!payload.uf) payload.uf = info.uf;
            if (!payload.email) payload.email = info.email;
            if (!payload.telefone && info.telefone) payload.telefone = info.telefone;

            if (info.descricao_situacao_cadastral && info.descricao_situacao_cadastral !== "ATIVA") {
              payload.status = FORNECEDOR_STATUS.PENDENTE;
              payload.motivo_rejeicao = "CNPJ inativo na Receita";
            }

            if (!payload.polo) {
              payload.polo = deduzePolo(info.bairro, info.municipio, info.logradouro);
            }
          }
          if (result.source === "api") {
            await new Promise(r => setTimeout(r, 1000));
          }
        } catch (err) {
          console.error("Enrichment failed for row", index, err);
        }
      }
      
      // CRITICAL: Filter out any keys not in the valid schema
      const finalPayload: any = {};
      Object.keys(payload).forEach(key => {
        if (VALID_FORNECEDORES_COLUMNS.includes(key)) {
          finalPayload[key] = payload[key];
        }
      });

      try {
        const normalizedName = finalPayload.nome ? finalPayload.nome.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "") : "";
        const normalizedDomain = finalPayload.site ? finalPayload.site.toLowerCase().replace(/^(https?:\/\/)?(www\.)?/, "").split('/')[0] : "";
        const cnpjClean = finalPayload.cnpj ? finalPayload.cnpj.replace(/\D/g, "") : null;

        const duplicate = fornecedoresExistentes?.find(f => {
          if (cnpjClean && f.cnpj && f.cnpj.replace(/\D/g, "") === cnpjClean) return true;
          if (!cnpjClean) {
            const existingName = f.nome.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
            const existingDomain = f.site ? f.site.toLowerCase().replace(/^(https?:\/\/)?(www\.)?/, "").split('/')[0] : "";
            return normalizedName === existingName && normalizedDomain === existingDomain && normalizedName !== "";
          }
          return false;
        });

        if (duplicate) {
          if (importConfig.politicaDuplicados === "atualizar") {
            const { error } = await supabase
              .from("fornecedores")
              .update(finalPayload)
              .eq("id", duplicate.id);
            if (error) throw error;
            atualizados++;
          } else {
            ignorados++;
          }
        } else {
          const { error } = await supabase.from("fornecedores").insert(finalPayload);
          if (error) throw error;
          inseridos++;
        }
      } catch (err: any) {
        erros.push({ line, error: err.message });
      }

      setImportProgress(Math.round(((index + 1) / total) * 100));
    }

    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from("importacoes").insert({
      admin_id: user?.id || null,
      nome_arquivo: "Importação Manual",
      total_linhas: total,
      inseridos,
      atualizados,
      ignorados,
      erros: (erros || []) as any
    });

    setResults({ inseridos, atualizados, ignorados, erros, criadosAuto });
    setIsImporting(false);
    queryClient.invalidateQueries({ queryKey: ["admin-fornecedores"] });
  };

  const downloadModelo = () => {
    const headers = ["Nome", "CNPJ", "Segmento", "Cidade", "UF", "WhatsApp", "Site", "Instagram", "E-mail"];
    const example1 = ["Fornecedor Exemplo", "12.345.678/0001-90", "Roupas", "São Paulo", "SP", "(11) 98888-7777", "www.exemplo.com", "exemplo_insta", "contato@exemplo.com"];
    const csvContent = [headers, example1].map(r => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "modelo_importacao_gopage.csv";
    link.click();
  };

  const downloadErros = () => {
    if (!results?.erros.length && !results?.abortError) return;
    
    let content = [];
    if (results?.abortError) {
      content = [{ error: `ERRO CRÍTICO NO INÍCIO DO LOTE: ${results.abortError.message} (${results.abortError.code})`, details: results.abortError.details, hint: results.abortError.hint }];
    } else {
      content = results!.erros.map(e => ({ 
        ...e.line, 
        erro_importacao: e.errors?.join("; ") || e.error 
      }));
    }
    
    const csvContent = Papa.unparse(content);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "erros_importacao.csv";
    link.click();
  };

  return (
    <div className="p-4 md:p-8 space-y-6 pb-24 md:pb-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Importação de Fornecedores</h2>
          <p className="text-muted-foreground">Adicione fornecedores em massa via CSV ou Excel.</p>
        </div>
        <Button variant="outline" onClick={downloadModelo}>
          <Download className="w-4 h-4 mr-2" />
          Baixar Planilha Modelo
        </Button>
      </div>

      {step === 1 && (
        <Card className="border-dashed border-2 bg-muted/30">
          <CardContent className="flex flex-col items-center justify-center py-16 space-y-4">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm">
              <Upload className="w-8 h-8 text-primary" />
            </div>
            <div className="text-center">
              <p className="text-lg font-medium">Arraste seu arquivo aqui ou clique para selecionar</p>
              <p className="text-sm text-muted-foreground">Formatos suportados: .csv, .xls, .xlsx</p>
            </div>
            <input 
              type="file" 
              className="hidden" 
              ref={fileInputRef} 
              accept=".csv, .xls, .xlsx"
              onChange={handleFileUpload}
            />
            <Button onClick={() => fileInputRef.current?.click()}>Selecionar Arquivo</Button>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Passo 1: Mapeamento de Colunas</CardTitle>
            <CardDescription>Relacione as colunas do seu arquivo com os campos do sistema.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {headers.map(header => (
                <div key={header} className="flex items-center justify-between p-3 border rounded-lg bg-muted/10">
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{header}</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300" />
                  <Select 
                    value={mapping[header] || "ignore"} 
                    onValueChange={(val) => setMapping(prev => ({ ...prev, [header]: val }))}
                  >
                    <SelectTrigger className="w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ignore">Ignorar Coluna</SelectItem>
                      <optgroup label="Campos Obrigatórios">
                        {COLUNAS_OBRIGATORIAS.map(c => (
                          <SelectItem key={c} value={c}>
                            {c === "nome" ? "Nome do Fornecedor *" : c === "cnpj" ? "CNPJ *" : c === "segmento" ? "Segmento *" : c}
                          </SelectItem>
                        ))}
                      </optgroup>
                      <optgroup label="Campos Opcionais">
                        {COLUNAS_OPCIONAIS.map(c => (
                          <SelectItem key={c} value={c}>
                            {c === "whatsapp" ? "WhatsApp" : c === "razao_social" ? "Razão Social" : c.charAt(0).toUpperCase() + c.slice(1).replace("_", " ")}
                          </SelectItem>
                        ))}
                      </optgroup>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </CardContent>
          <CardFooter className="justify-end">
            <Button onClick={() => setStep(3)}>Pré-visualizar e Validar</Button>
          </CardFooter>
        </Card>
      )}

      {step === 3 && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Passo 2: Pré-visualização e Configuração</CardTitle>
              <CardDescription>Verificando as primeiras 20 linhas para erros comuns.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto border rounded-lg mb-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {Object.values(mapping).map(m => m !== "ignore" && <TableHead key={m} className="capitalize">{m}</TableHead>)}
                      <TableHead>Validação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rawData.slice(0, 20).map((row, idx) => {
                      const { errors, warnings } = validateLine(row);
                      return (
                        <TableRow key={idx}>
                          {Object.entries(mapping).map(([csv, db]) => (
                            db !== "ignore" && <TableCell key={db} className="text-sm">{row[csv]}</TableCell>
                          ))}
                          <TableCell>
                            {errors.length > 0 ? (
                              <div className="flex items-center gap-1 text-red-600 text-xs">
                                <XCircle className="w-3 h-3" /> {errors[0]}
                              </div>
                            ) : warnings.length > 0 ? (
                              <div className="flex items-center gap-1 text-amber-600 text-xs">
                                <AlertTriangle className="w-3 h-3" /> 
                                <span className={cn(warnings[0]?.includes("será criado") && "bg-amber-100 px-1.5 py-0.5 rounded text-amber-900 font-medium")}>
                                  {warnings[0]}
                                </span>
                              </div>
                            ) : (
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>Status de entrada</Label>
                  <Select value={importConfig.status} onValueChange={(val) => setImportConfig(prev => ({ ...prev, status: val }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={FORNECEDOR_STATUS.PENDENTE}>Pendente (Moderação)</SelectItem>
                      <SelectItem value={FORNECEDOR_STATUS.APROVADO}>Aprovado Direto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Política de Duplicados (CNPJ)</Label>
                  <Select value={importConfig.politicaDuplicados} onValueChange={(val: any) => setImportConfig(prev => ({ ...prev, politicaDuplicados: val }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ignorar">Ignorar Duplicados</SelectItem>
                      <SelectItem value="atualizar">Atualizar Existentes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between p-4 bg-primary/10 border border-primary/20 rounded-xl">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-sm font-bold">Marcar como verificado</p>
                        <p className="text-xs text-muted-foreground">Use apenas se todos os fornecedores da planilha forem clientes Gopage.</p>
                      </div>
                    </div>
                    <Switch 
                      checked={importConfig.verificado} 
                      onCheckedChange={(val) => setImportConfig(prev => ({ ...prev, verificado: val }))} 
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch 
                      checked={importConfig.enriquecerCnpj} 
                      onCheckedChange={(val) => setImportConfig(prev => ({ ...prev, enriquecerCnpj: val }))} 
                    />
                    <Label>Buscar cidade, UF, telefone e razão social (via CNPJ)</Label>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="justify-between border-t pt-6">
              <Button variant="ghost" onClick={() => setStep(2)}>Voltar</Button>
              <Button onClick={startImport}>Iniciar Importação de {rawData.length} linhas</Button>
            </CardFooter>
          </Card>
        </div>
      )}

      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle>
              {isImporting 
                ? "Importando dados..." 
                : results?.abortError 
                  ? "Importação Abortada" 
                  : "Importação Concluída"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 py-10">
            {isImporting ? (
              <div className="space-y-4">
                <Progress value={importProgress} className="h-4" />
                <p className="text-center text-muted-foreground font-medium">
                  {importConfig.enriquecerCnpj ? "Enriquecendo e importando..." : "Importando..."} {Math.round((importProgress / 100) * rawData.length)} de {rawData.length}...
                </p>
                <div className="flex justify-center">
                  <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
                    Cancelar Importação
                  </Button>
                </div>
              </div>
            ) : results?.abortError ? (
              <div className="p-6 bg-red-50 border border-red-200 rounded-xl space-y-4">
                <div className="flex items-center gap-3 text-red-700">
                  <AlertTriangle className="w-6 h-6" />
                  <h3 className="text-lg font-bold">Falha no teste de conexão/schema</h3>
                </div>
                <div className="space-y-2 text-sm text-red-600">
                  <p><strong>Mensagem:</strong> {results.abortError.message}</p>
                  <p><strong>Código:</strong> {results.abortError.code}</p>
                  {results.abortError.details && <p><strong>Detalhes:</strong> {results.abortError.details}</p>}
                  {results.abortError.hint && <p><strong>Dica:</strong> {results.abortError.hint}</p>}
                </div>
                <details className="mt-4">
                  <summary className="text-xs font-mono cursor-pointer hover:underline text-red-500">Ver detalhes técnicos (JSON)</summary>
                  <pre className="mt-2 p-3 bg-white border border-red-100 rounded text-[10px] overflow-auto max-h-40">
                    {JSON.stringify(results.abortError, null, 2)}
                  </pre>
                </details>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-green-50 rounded-xl border border-green-100 text-center">
                  <p className="text-sm text-green-600 font-medium">Inseridos</p>
                  <p className="text-3xl font-bold text-green-700">{results?.inseridos}</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 text-center">
                  <p className="text-sm text-blue-600 font-medium">Atualizados</p>
                  <p className="text-3xl font-bold text-blue-700">{results?.atualizados}</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-100 text-center">
                  <p className="text-sm text-amber-600 font-medium">Ignorados</p>
                  <p className="text-3xl font-bold text-amber-700">{results?.ignorados}</p>
                </div>
                <div className="p-4 bg-red-50 rounded-xl border border-red-100 text-center">
                  <p className="text-sm text-red-600 font-medium">Erros</p>
                  <p className="text-3xl font-bold text-red-700">{results?.erros.length}</p>
                </div>
              </div>
            )}
            
            {results && results.criadosAuto && results.criadosAuto.length > 0 && (
              <div className="p-4 bg-primary/5 border border-primary/10 rounded-xl">
                <div className="flex items-center gap-2 text-primary font-medium mb-2">
                  <Plus className="w-4 h-4" />
                  Segmentos criados automaticamente ({results.criadosAuto.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {results.criadosAuto.map((name: string) => (
                    <Badge key={name} variant="outline" className="bg-white">{name}</Badge>
                  ))}
                </div>
                <Button 
                  variant="link" 
                  size="sm" 
                  className="mt-2 h-auto p-0 text-xs gap-1"
                  onClick={() => window.location.href = "/admin/segmentos"}
                >
                  Gerenciar em /admin/segmentos <ChevronRight className="w-3 h-3" />
                </Button>
              </div>
            )}
          </CardContent>
          {!isImporting && (
            <CardFooter className="justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>Nova Importação</Button>
              <div className="flex gap-2">
                {results?.erros.length! > 0 && (
                  <Button variant="destructive" onClick={downloadErros}>
                    <Download className="w-4 h-4 mr-2" /> Baixar Linhas com Erro
                  </Button>
                )}
                <Button onClick={() => window.location.href = "/admin/fornecedores"}>Ver Fornecedores</Button>
              </div>
            </CardFooter>
          )}
        </Card>
      )}
    </div>
  );
}
